import { FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CredentialDocument } from "../../types";

/** Short human hint for the document's OCR lifecycle
 * (`credentialDocument.ocrStatus` — see `CredentialDocumentOcrStatus` in
 * api/src/db/schema/enums.ts). `none` renders no hint. */
function ocrStatusHint(status: CredentialDocument["ocrStatus"]): string | null {
  switch (status) {
    case "pending":
      return "Processing…";
    case "parsed":
      return "Parsed";
    case "error":
      return "Couldn't process";
    default:
      return null;
  }
}

/**
 * One document on a credential.
 *
 * The row itself opens the preview — the underlined title is what signals that,
 * since a file name that does nothing when clicked is the more surprising
 * default. The preview carries its own download button, so the row does not
 * repeat it.
 *
 * The clickable area is a real `<button>` rather than a click handler on the
 * wrapper: it keeps the row reachable by keyboard, and it keeps Remove out of
 * the target instead of nesting one control inside another.
 *
 * The signed link is minted per click rather than rendered as an `href` — a URL
 * sitting in the DOM outlives the click, and these point at medical credentials.
 */
export function DocumentRow({
  document,
  onOpen,
  isOpening = false,
  onRemove,
}: {
  document: CredentialDocument;
  /** Absent on surfaces with no document-read endpoint wired; the row then
   * shows what is on file without offering to open it. */
  onOpen?: (document: CredentialDocument) => void;
  isOpening?: boolean;
  /** Supplied where the reader may edit. Takes the document off this
   * credential; the file and the record of it having been attached remain. */
  onRemove?: (document: CredentialDocument) => void;
}) {
  const hint = ocrStatusHint(document.ocrStatus);
  const name = document.title || document.fileName;

  const body = (
    <>
      <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span
          className={
            onOpen
              ? "block truncate text-sm font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 group-hover:decoration-slate-600"
              : "block truncate text-sm font-medium text-slate-900"
          }
        >
          {name}
        </span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </>
  );

  return (
    <div className="flex items-center gap-1 py-1.5">
      {onOpen ? (
        <button
          type="button"
          disabled={isOpening}
          onClick={() => onOpen(document)}
          // Labelled explicitly rather than by its contents: the visible text
          // is the file name plus an OCR hint, which reads as a run-on to a
          // screen reader and never says what the control does.
          aria-label={`View ${name}`}
          // A raw <button> does not inherit the pointer cursor the shared
          // Button component applies, and without it the row reads as text.
          className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded text-left disabled:cursor-wait disabled:opacity-60"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2.5">{body}</div>
      )}

      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 shrink-0 px-0 text-slate-400 hover:text-red-600"
          title="Remove from this credential"
          onClick={() => onRemove(document)}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">Remove {name}</span>
        </Button>
      )}
    </div>
  );
}
